import type { LangCode } from "@/lib/gql/__generated__/graphql";

import type { FeatureIslandHeaderLangNavigatorProps } from "@/components/features/_layouts";
import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithCategory,
	FeatureIslandMainWithCategoryWrapper,
} from "@/components/features/_layouts";

import { useFetchCustomMailSettingI18n } from "../api";
import { useCustomMailSettingForm } from "../hooks";
import { customMailSettingI18nCategoriesAndManagerStore } from "../store";
import { ForCheckInRemind } from "./custom-mail-setting-form/for-check-in-remind";
import { ForCheckOutRemind } from "./custom-mail-setting-form/for-check-out-remind";
import { ForRequestReservation } from "./custom-mail-setting-form/for-request-reservation";
import { ForShopping } from "./custom-mail-setting-form/for-shopping";
import { ForTriggeredByReserve } from "./custom-mail-setting-form/for-triggered-by-reserve";
import { HeaderInner } from "./header-inner";
import { HeaderRightButton } from "./header-right-button";

type Props = {
	hotelID: string;
	langCode: LangCode;
	navigator: FeatureIslandHeaderLangNavigatorProps;
};

export const CustomMailSettingI18n = ({ hotelID, langCode, navigator }: Props) => {
	// dataの取得
	const { data } = useFetchCustomMailSettingI18n({ hotelID, langCode });

	const { form } = useCustomMailSettingForm({
		hotelID,
		customMailSetting: data?.basicCustomMailSettingI18n ?? undefined,
		langCode,
	});

	const {
		forReservation,
		forCheckInRemind,
		forCheckOutRemind,
		forRequestReservation,
		forShopping,
		categoryManagerStore,
	} = customMailSettingI18nCategoriesAndManagerStore;

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader
				title="基本メール設定の編集"
				activeLangCode={langCode}
				navigator={navigator}
				rightChildren={<HeaderRightButton form={form} />}
			/>
			<HeaderInner hotelID={hotelID} />
			<FeatureIslandMainWithCategory categoryManagerStore={categoryManagerStore}>
				{/* 予約：確定・変更・キャンセルメールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forReservation}
					islandTitle="予約：確定・変更・キャンセルメールの内容設定"
					tooltipTitle="ゲストに送信されるメールの内容をカスタマイズできます。<br /> テキストのみの設定となります。"
				>
					<ForTriggeredByReserve form={form} hotelID={hotelID} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* チェックイン前：リマインドメールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forCheckInRemind}
					islandTitle="チェックイン前：リマインドメールの内容設定"
					tooltipTitle="チェックイン前にゲストに送信されるメールの内容をカスタマイズできます。<br /> テキストのみの設定となります。"
				>
					<ForCheckInRemind form={form} hotelID={hotelID} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* チェックアウト後：ご案内メール設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forCheckOutRemind}
					islandTitle="チェックアウト後：ご案内メール設定"
					tooltipTitle="チェックアウト後にゲストに送信されるメールの内容をカスタマイズできます。<br /> テキストのみの設定となります。"
				>
					<ForCheckOutRemind form={form} hotelID={hotelID} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* リクエスト予約メールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forRequestReservation}
					islandTitle="リクエスト予約メールの内容設定"
					tooltipTitle="リクエスト予約時にゲストに送信されるメールの内容をカスタマイズできます。<br /> ※このメールを送信オフにすることはできません。<br /> テキストのみの設定となります。"
				>
					<ForRequestReservation form={form} hotelID={hotelID} />
				</FeatureIslandMainWithCategoryWrapper>
				{/* 宿泊券：ゲストに送信されるメールの内容設定 */}
				<FeatureIslandMainWithCategoryWrapper
					categoryStore={forShopping}
					islandTitle="宿泊券：ゲストに送信されるメールの内容設定"
					tooltipTitle="宿泊券購入時にゲストに送信されるメールの内容をカスタマイズできます。<br /> ※このメールを送信オフにすることはできません。<br /> テキストのみの設定となります。"
				>
					<ForShopping form={form} />
				</FeatureIslandMainWithCategoryWrapper>
			</FeatureIslandMainWithCategory>
		</FeatureIslandContainer>
	);
};
